import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import MainLayout from '@/components/templates/MainLayout.vue'
import { useFeatureFlags } from '@/composables/useFeatureFlags'
import { useAuthStore } from '@/stores/auth.store'
import { canViewDashboard, type AccessLevel } from '@/types/auth.types'
import { agendaOpsRoutes } from './agendaOps.routes'
import { commsRoutes } from './comms.routes'
import { partnerRoutes } from './partner.routes'
import { pipelineRoutes } from './pipeline.routes'
import { pmsRoutes } from './pms.routes'
import { portalRoutes } from './portal.routes'

/**
 * Cold-launch-while-offline branch.
 *
 * When the SPA boots without a network and the partner isn't yet
 * authenticated, the auth bootstrap (`auth.init()` → `/auth/me`)
 * will fail. Without this guard the router would deflect to
 * `/login`, which itself can't reach the auth endpoints offline —
 * the partner ends up on a confusing white page.
 *
 * The predicate is intentionally pure so it tests without spinning
 * a real router. Public routes (login, /offline itself, error
 * pages) are excluded so the partner can still reach them when the
 * network is up later.
 */
export function shouldShowOfflineFallback(
  to: RouteLocationNormalized,
  isAuthenticated: boolean,
  isOnline: boolean,
): boolean {
  if (to.meta.public === true) return false
  if (isAuthenticated) return false
  if (isOnline) return false
  return true
}

// Convenience aliases (extend as you add role-gated routes)
const ALL: AccessLevel[] = ['access_level.admin', 'access_level.manager', 'access_level.user']
const ADMIN_MANAGER: AccessLevel[] = ['access_level.admin', 'access_level.manager']
const ADMIN: AccessLevel[] = ['access_level.admin']

/** Resolve the home path for a role. Used both by the `/dashboard`
 *  redirect and by the "blocked from this route" deflection in the
 *  navigation guard. Keeping a single source of truth prevents the
 *  loop where `user`-role traffic deflects to a route they can't
 *  reach (`/pms/agenda`) which deflects again — the bug
 *  `router-dashboard-deep-routes.spec.ts` documented before plan 3. */
function homePathFor(level: AccessLevel | undefined): string {
  if (level === 'access_level.partner') return '/portal'
  if (level === 'access_level.admin' || level === 'access_level.manager') return '/pms/agenda'
  // `access_level.user` and any unknown / undefined level: send to the
  // only authenticated route they can read (their own profile).
  return '/account'
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // PMS (back-office) routes — owned by frontend/src/router/pms.routes.ts.
    ...pmsRoutes,
    // Full-app-completion feature routes — one file per Phase-2 agent
    // (fe-partner / fe-agenda / fe-model / fe-comms). Registered here
    // ONCE by fe-nav; agents extend their own file, never this one.
    ...partnerRoutes,
    ...agendaOpsRoutes,
    ...pipelineRoutes,
    ...commsRoutes,
    // Partner portal routes — owned by frontend/src/router/portal.routes.ts.
    ...portalRoutes,
    // Auth routes (outside any shell)
    //
    // `/login` is now the partner-portal email + OTP two-step view.
    // The legacy Google OAuth view lives at `/login/backoffice` so
    // backoffice users can still authenticate. The two intermediate
    // OTP routes (`/login/otp/request`, `/login/otp/verify`) were
    // dropped when the in-page two-step replaced them.
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/PortalLoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/login/backoffice',
      name: 'login-backoffice',
      component: () => import('@/views/auth/BackofficeLoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/views/auth/AuthCallbackView.vue'),
      meta: { public: true },
    },
    {
      // Coming-soon page rendered when PARTNER_DASHBOARD_ENABLED is false.
      // Public so partners reach it without an authenticated session.
      path: '/dashboard-disabled',
      name: 'dashboard-disabled',
      component: () => import('@/views/partner/DashboardDisabledView.vue'),
      meta: { public: true },
    },
    // Phase-3 cutover: `/dashboard` is now a per-role redirect. The view
    // tree it used to point at lives under `/pms/*` and `/portal/*`.
    {
      path: '/dashboard',
      name: 'dashboard',
      beforeEnter: () => {
        const auth = useAuthStore()
        return homePathFor(auth.currentUser?.access_level)
      },
      // The redirect runs in beforeEnter; we still need a component to
      // satisfy vue-router's typing. The empty render never paints
      // because `beforeEnter` returns a different path.
      component: { render: () => null },
    },
    {
      path: '/',
      component: MainLayout,
      children: [
        {
          path: '/account',
          name: 'account',
          component: () => import('@/views/Account.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/admin/partner-import',
          name: 'admin-partner-import',
          component: () => import('@/views/admin/PartnerImportView.vue'),
          meta: { access_levels: ADMIN_MANAGER },
        },
        {
          path: '/admin/sync',
          name: 'admin-sync',
          component: () => import('@/views/admin/SyncView.vue'),
          meta: { access_levels: ADMIN },
        },
        {
          path: '/admin/users',
          name: 'admin-users',
          component: () => import('@/views/admin/UsersView.vue'),
          meta: { access_levels: ADMIN },
        },
        // Add domain-entity routes here — see docs/guides/adding-entities.md
      ],
    },
    // Error pages — public so unauthenticated visitors see a styled page
    // instead of a redirect-to-login bounce. The catch-all MUST be the
    // LAST route in the array; vue-router matches in order.
    {
      path: '/500',
      name: 'server-error',
      component: () => import('@/views/errors/ServerErrorView.vue'),
      meta: { public: true },
    },
    {
      // Cold-launch-offline target. Reached either via the
      // `shouldShowOfflineFallback` branch in the auth guard or by a
      // direct `router.push('/offline')` somewhere in the app.
      path: '/offline',
      name: 'offline',
      component: () => import('@/views/errors/OfflineView.vue'),
      meta: { public: true },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/errors/NotFoundView.vue'),
      meta: { public: true },
    },
  ],
})

// Navigation guard — redirect to login if not authenticated, check role access
let authInitialized = false

/** Called from main.ts after auth.init() completes */
export function markAuthInitialized() {
  authInitialized = true
}

router.beforeEach(async (to) => {
  if (to.meta.public) return true

  const authStore = useAuthStore()

  // Cold-launch-offline branch — checked BEFORE `authStore.init()`
  // because init() round-trips to `/auth/me` and would hang/fail on
  // an offline boot. `navigator.onLine` is best-effort but enough
  // for this fallback: a false positive routes the partner to
  // /login (existing behaviour); a true positive routes them to
  // /offline (the new path). Either is better than a white screen.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    if (shouldShowOfflineFallback(to, authStore.isAuthenticated, false)) {
      return { name: 'offline', query: { from: to.fullPath } }
    }
  }

  // On first navigation (page load), auth might not be initialized yet.
  // Wait for it — init() is called in main.ts before mount, but the
  // router may start resolving before mount completes.
  if (!authInitialized) {
    try {
      await authStore.init()
    } catch {
      // init failed — will redirect to login below
    }
    authInitialized = true
  }

  if (!authStore.isAuthenticated) {
    return { name: 'login' }
  }

  // Role-based access check. When the user can't reach the requested
  // route, send them to their own home — which is now role-specific
  // (see homePathFor). Returning a path here means the guard re-runs
  // for the new target, so role gates on that target still apply.
  const requiredRoles = to.meta.access_levels as AccessLevel[] | undefined
  if (requiredRoles && authStore.currentUser) {
    if (!requiredRoles.includes(authStore.currentUser.access_level)) {
      const home = homePathFor(authStore.currentUser.access_level)
      // If the deflection target is the route the user is already
      // trying to reach, abort to prevent a loop. (Reachable only if
      // a misconfigured route table sets `access_levels` so tight
      // that even the role's home is excluded — defensive.)
      if (home === to.path) return false
      return { path: home }
    }
  }

  // `leader_only` routes (the PMS Dashboard) open to admins and leaders;
  // everyone else who reached the PMS lands on the agenda.
  if (to.meta.leader_only === true && !canViewDashboard(authStore.currentUser)) {
    return { path: '/pms/agenda' }
  }

  // Feature-flag fallback for partner traffic — when the partner dashboard
  // is disabled, deflect partner users to the friendly Coming-Soon page.
  if (authStore.currentUser?.access_level === 'access_level.partner') {
    const ff = useFeatureFlags()
    await ff.ensureLoaded()
    if (!ff.partnerDashboardEnabled.value && to.name !== 'dashboard-disabled') {
      return { name: 'dashboard-disabled' }
    }
  }

  return true
})

export default router
